let pdfJsPromise;
let pdfJsWorker;

const loadPdfJs = async () => {
  if (!pdfJsPromise) {
    pdfJsPromise = import("pdfjs-dist/webpack.mjs").catch((error) => {
      pdfJsPromise = undefined;
      throw error;
    });
  }
  return pdfJsPromise;
};

const assetRootUrl = () => {
  const configured = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  if (configured && configured !== ".") return new URL(`${configured}/`, window.location.href);
  // Resolve a relative CRA homepage against its bundle, not the client route.
  const bundle = Array.from(document.scripts).find((script) => (
    /\/static\/js\/(?:main(?:\.[^/]+)?|bundle)\.js$/.test(new URL(script.src || "", window.location.href).pathname)
  ));
  return bundle ? new URL("../../", bundle.src) : new URL("/", window.location.href);
};

const withPdfDocument = async (file, callback) => {
  const pdfjs = await loadPdfJs();
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (!pdfJsWorker || pdfJsWorker.destroyed) {
    pdfJsWorker = pdfjs.PDFWorker.create({ port: pdfjs.GlobalWorkerOptions.workerPort });
  }
  // Django collects build/static, so these assets live beside static/js.
  const assetBase = new URL(`static/pdfjs/${pdfjs.version}/`, assetRootUrl());
  const task = pdfjs.getDocument({
    data: bytes,
    // The webpack entry shares one worker. Tasks release only their documents;
    // finishing one preview must not destroy another preview's active worker.
    worker: pdfJsWorker,
    isEvalSupported: false,
    cMapUrl: new URL("cmaps/", assetBase).href,
    cMapPacked: true,
    standardFontDataUrl: new URL("standard_fonts/", assetBase).href,
    wasmUrl: new URL("wasm/", assetBase).href,
  });
  try {
    return await callback(await task.promise);
  } finally {
    // PDF.js 6 exposes teardown on the loading task, including failed loads.
    await task.destroy();
  }
};

const renderPage = async (page, { targetWidth, scale, rotation = 0, type = "dataUrl" }) => {
  const outputRotation = ((Number(page.rotate) || 0) + rotation + 360) % 360;
  const baseViewport = page.getViewport({ scale: 1, rotation: outputRotation });
  const renderScale = scale || Math.max(0.1, targetWidth / baseViewport.width);
  const viewport = page.getViewport({ scale: renderScale, rotation: outputRotation });
  const pixelRatio = type === "dataUrl" ? Math.min(window.devicePixelRatio || 1, 1.5) : 1;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { alpha: false });
  canvas.width = Math.ceil(viewport.width * pixelRatio);
  canvas.height = Math.ceil(viewport.height * pixelRatio);
  await page.render({
    canvasContext: context,
    viewport,
    transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0],
    background: "white",
  }).promise;

  if (type === "blob") {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("PNG 產生失敗"))),
        "image/png",
      );
    });
  }
  return canvas.toDataURL("image/jpeg", 0.78);
};

export const renderPdfThumbnails = (file, pageCount) => withPdfDocument(file, async (document) => {
  const thumbnails = [];
  for (let index = 0; index < pageCount; index += 1) {
    const page = await document.getPage(index + 1);
    try {
      thumbnails.push(await renderPage(page, { targetWidth: 180 }));
    } finally {
      page.cleanup();
    }
  }
  return thumbnails;
});

export const renderPdfPagePreview = (file, pageIndex, rotation = 0) => withPdfDocument(file, async (document) => {
  const page = await document.getPage(pageIndex + 1);
  try {
    return await renderPage(page, { targetWidth: 1100, rotation });
  } finally {
    page.cleanup();
  }
});

const textItemsToLines = (items) => {
  const lines = [];
  let line = "";
  let previousY = null;

  items.forEach((item) => {
    const value = String(item.str || "");
    const y = Number(item.transform?.[5]);
    const startsNewLine = previousY !== null && Number.isFinite(y) && Math.abs(y - previousY) > 2;
    if (startsNewLine && line.trim()) {
      lines.push(line.trimEnd());
      line = "";
    }
    if (line && value && !/^\s/.test(value) && !/\s$/.test(line)) line += " ";
    line += value;
    if (item.hasEOL && line.trim()) {
      lines.push(line.trimEnd());
      line = "";
    }
    if (Number.isFinite(y)) previousY = y;
  });
  if (line.trim()) lines.push(line.trimEnd());
  return lines.join("\n").trim();
};

export const extractPdfTextPages = (file, pageIndices) => withPdfDocument(file, async (document) => {
  const result = {};
  for (const pageIndex of [...new Set(pageIndices)]) {
    const page = await document.getPage(pageIndex + 1);
    try {
      const content = await page.getTextContent();
      result[pageIndex] = textItemsToLines(content.items);
    } finally {
      page.cleanup();
    }
  }
  return result;
});

export const renderPdfPagesAsPng = (file, pageSpecs) => withPdfDocument(file, async (document) => {
  const result = [];
  for (const spec of pageSpecs) {
    const page = await document.getPage(spec.pageIndex + 1);
    try {
      result.push({
        id: spec.id,
        blob: await renderPage(page, { scale: 2, rotation: spec.rotation || 0, type: "blob" }),
      });
    } finally {
      page.cleanup();
    }
  }
  return result;
});

// Expose selectable text runs in the same coordinate space as the preview.
// Scanned pages have no text runs; the editor offers a covering text box there.
export const extractPdfEditableText = (file, pageIndex) => withPdfDocument(file, async (document) => {
  const page = await document.getPage(pageIndex + 1);
  try {
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    return content.items.flatMap((item, index) => {
      if (!item.str?.trim() || !item.transform || !item.width) return [];
      const [a, b, c, d, e, f] = item.transform;
      const fontSize = Math.hypot(c, d) || Math.hypot(a, b) || item.height || 12;
      const style = content.styles?.[item.fontName] || {};
      const ascent = style.ascent ?? (style.descent ? 1 + style.descent : .8);
      const baselineLength = Math.hypot(a, b) || 1;
      const ux = a / baselineLength; const uy = b / baselineLength;
      const vx = c / fontSize; const vy = d / fontSize;
      const corners = [[0, ascent], [item.width, ascent], [0, ascent - 1], [item.width, ascent - 1]]
        .map(([along, up]) => viewport.convertToViewportPoint(e + ux * along + vx * up * fontSize, f + uy * along + vy * up * fontSize));
      const left = Math.max(0, Math.min(...corners.map(([x]) => x)) - 1);
      const top = Math.max(0, Math.min(...corners.map(([, y]) => y)) - 1);
      const right = Math.min(viewport.width, Math.max(...corners.map(([x]) => x)) + 1);
      const bottom = Math.min(viewport.height, Math.max(...corners.map(([, y]) => y)) + 1);
      if (right <= left || bottom <= top) return [];
      const origin = viewport.convertToViewportPoint(e, f);
      const direction = viewport.convertToViewportPoint(e + ux, f + uy);
      const textAngle = Math.atan2(direction[1] - origin[1], direction[0] - origin[0]) * 180 / Math.PI;
      return [{ id: `source-text-${index}`, text: item.str, fontSize, textAngle,
        x: left / viewport.width, y: top / viewport.height,
        width: (right - left) / viewport.width, height: (bottom - top) / viewport.height }];
    });
  } finally { page.cleanup(); }
});

export const getPdfPageCount = (file) => withPdfDocument(file, async (document) => document.numPages);
